
import { Component, Emit, Mixins, Prop, Watch } from 'vue-property-decorator'
import {
  IOnboardingPayload,
  IOnboardingRestaurant
} from '@/firestore'
import AuthMixin from '@/mixins/AuthMixin.vue'
import PhoneNumber from '@/components/PhoneNumber.vue'
import AddressEdit from '@/components/Address/AddressEdit.vue'
import axios from 'axios'
import LabeledValue from '@/components/LabeledValue.vue'
import LiquorLicenseFields from '@/components/LiquorLicense/LiquorLicenseFields.vue'
import { licenseService } from '../LiquorLicense/liquor-license'

@Component({
  components: {PhoneNumber, AddressEdit, LabeledValue, LiquorLicenseFields}
})
export default class AddRestaurantModal extends Mixins(AuthMixin) {
  @Prop({type: Boolean, default: false})
  public show!: boolean

  @Prop({type: Object, default: null})
  public onboarding!: IOnboardingPayload

  @Prop({type: Object, default: null})
  public restaurant!: IOnboardingRestaurant

  @Prop({type: String, default: 'create'})
  public action!: 'create' | 'update'

  public $refs!: {
    form: HTMLFormElement
  }

  public valid: boolean = false

  public licenseNumber = ''
  public originalLicenseNumber = ''
  public licenseFile: File | null = null
  private originalLicenseFile: File | null = null

  public saveLoading = false

  public licenseRules = [
    (v: File) => !v || v.size < 10 * 1000000 || 'File size must be less than 10MB'
  ]

  public validateChefOrGM() {
    if (
        this.restaurant.chef
        && !this.restaurant.chef.name
        && !this.restaurant.chef.phoneNumber
        && this.restaurant.generalManager
        && !this.restaurant.generalManager.name
        && !this.restaurant.generalManager.phoneNumber
    ) {
      return 'Please enter either Chef and/or General Manager Information'
    }

    return true
  }

  public validateChefName(v: string) {
    if (!v && this.restaurant.chef && this.restaurant.chef.phoneNumber) {
      return `Please enter your Chef's Name`
    }
    return true
  }

  public validateChefPhone(v: string) {
    if (
        (!v && this.restaurant.chef && this.restaurant.chef.name)
        || (v && !/^\+\d+ \([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(v))
    ) {
      return `Please enter your Chef's 10 digit Phone Number`
    }

    return true
  }

  public validateGMName(v: string) {
    if (!v && this.restaurant.generalManager && this.restaurant.generalManager.phoneNumber) {
      return `Please enter your General Manager's Name`
    }
    return true
  }

  public validateGMPhone(v: string) {
    if (
        (!v && this.restaurant.generalManager && this.restaurant.generalManager.name)
        || (v && !/^\+\d+ \([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(v))
    ) {
      return `Please enter your General Manager's 10 digit Phone Number`
    }

    return true
  }

  public async mounted() {
  }

  @Emit()
  public cancel() {
  }

  public async confirm() {
    if (this.$refs.form) {
      this.$refs.form.validate()
    }
    if (this.valid) {
      if (
          this.licenseNumber !== this.originalLicenseNumber
          || this.licenseFile !== this.originalLicenseFile
      ) {
        await (
            this.licenseFile
                ? this.uploadLicenseFile(this.licenseFile)
                : this.removeLicenseFile()
        )
      }

      this.$emit('confirm')
    }
  }

  @Watch('show')
  public resetValidation() {
    if (this.$refs.form) {
      this.$refs.form.resetValidation()
    }
  }

  @Watch('show')
  public setupLicenseRefs() {
    if (this.restaurant.liquorLicense) {
      const licenseInfo = this.restaurant.liquorLicense
      if (
          licenseInfo.filename
          && licenseInfo.mimeType
          && licenseInfo.size
          && licenseInfo.createdAt
      ) {
        const licensePlaceholder = licenseService.placeholderFile(
            licenseInfo.filename,
            licenseInfo.mimeType,
            +licenseInfo.size || 0,
            licenseInfo.createdAt
        )
        this.originalLicenseFile = licensePlaceholder
        this.licenseFile = licensePlaceholder
      } else {
        this.originalLicenseFile = null
      }
      const licenseNumber = licenseInfo.licenseNumber || ''
      this.originalLicenseNumber = licenseNumber
      this.licenseNumber = licenseNumber
    } else {
      this.originalLicenseFile = null
      this.licenseFile = null
    }
  }

  private async uploadLicenseFile(file: File) {
    const token = this.restaurant.liquorLicense
        ? this.restaurant.liquorLicense.token
        : null
    const fileId = this.restaurant.liquorLicense && this.licenseFile === this.originalLicenseFile
        ? this.restaurant.liquorLicense.fileId
        : undefined

    this.saveLoading = true

    const formData = new FormData()
    formData.append('file', file)
    formData.append('data', JSON.stringify({
      groupName: this.onboarding.groupName,
      restaurantName: this.restaurant.name,
      licenseNumber: this.licenseNumber,
      fileId,
      token
    }))

    this.restaurant.liquorLicense = (await axios.post(
        '/api/onboarding/liquor-license',
        formData,
        {headers: {'content-type': 'multipart/form-data'}}
    )).data as IOnboardingRestaurant['liquorLicense']

    this.saveLoading = false
  }

  private async removeLicenseFile() {
    if (this.restaurant.liquorLicense) {
      this.saveLoading = true
      try {
        await axios.delete(
            '/api/onboarding/liquor-license',
            {data: {token: this.restaurant.liquorLicense.token}}
        )
      } catch (err) {
        console.error(err)
      }
      delete this.restaurant.liquorLicense
      this.saveLoading = false
    }
  }
}
